import { MenuItem, Select } from "@mui/material";
import Loading from "components/Loading/Loading/Loading";
import CryptoJS from "crypto-js";
import {
  browserSessionPersistence,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { collection, getDocs } from "firebase/firestore";
import { useEffect, useState } from "react";
import { auth, db } from "../../firebase";

const SECRET_KEY = process.env.REACT_APP_ESTIMATE_SECRET_KEY;
const COMPANY_EMAIL = "therapscompany@gmail.com";

const EstimateLogin = ({ onSuccess }) => {
  const [branchList, setBranchList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedBranch) {
      setError("지점을 선택해주세요.");
      return;
    }
    if (!password) {
      setError("비밀번호를 입력해주세요.");
      return;
    }

    const branch = branchList.find((branch) => branch.id === selectedBranch);
    const hashedPassword = CryptoJS.SHA256(password).toString();
    if (branch.password === hashedPassword) {
      setIsLoading(true);
      // 본사일 때 Firebase 로그인
      if (branch.name === "본사") {
        try {
          await setPersistence(auth, browserSessionPersistence); // 세션 탭 닫으면 로그아웃
          await signInWithEmailAndPassword(auth, COMPANY_EMAIL, password); // Firebase 로그인
        } catch (err) {
          console.error("Firebase Auth 로그인 실패:", err);
          setError("본사 계정 인증에 실패했습니다.");
          setIsLoading(false);
          return;
        }
      }

      // 세션 저장
      const value = branch.name;
      const signature = CryptoJS.HmacSHA256(value, SECRET_KEY).toString();
      sessionStorage.setItem("branch", value);
      sessionStorage.setItem("branch_sig", signature);
      onSuccess();
    } else {
      setError("비밀번호가 올바르지 않습니다.");
      setIsLoading(false);
    }
  };

  // firebase에서 지점 목록 가져오기
  const fetchBranchList = async () => {
    try {
      setIsLoading(true);
      const itemsRef = collection(db, "deploy", "estimate", "branches");
      const snapshot = await getDocs(itemsRef);

      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      items.sort((a, b) => a.order - b.order);
      setBranchList(items);
    } catch (err) {
      console.log("지점 목록 로드 실패:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBranchList();
  }, []);

  return (
    <>
      {isLoading && <Loading />}
      <div style={styles.wrapper}>
        <div style={styles.card}>
          {/* 로고/타이틀 */}
          <div style={styles.titleArea}>
            <img src={require("../../assets/main_icon.png")} alt="RAPS" style={styles.logo} />
            <h1 style={styles.title}>RAPS Estimate</h1>
            <p style={styles.subtitle}>기자재 견적 요청 시스템</p>
          </div>

          {/* 지점 선택 */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>지점 선택</label>
            <Select
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setError("");
              }}
              displayEmpty
              size="small"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 220,
                  },
                },
              }}
              style={styles.select}
            >
              <MenuItem value="" disabled>
                <span style={{ color: "#aaa" }}>- 지점을 선택하세요 -</span>
              </MenuItem>
              {branchList.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </div>

          {/* 비밀번호 */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>비밀번호</label>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              placeholder="비밀번호를 입력하세요"
              style={styles.input}
            />
          </div>

          {/* 에러 메시지 */}
          {error && <p style={styles.error}>{error}</p>}

          <button onClick={handleSubmit} style={styles.button}>
            로그인
          </button>
        </div>
      </div>
    </>
  );
};

const styles = {
  wrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    backgroundColor: "#10134F",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "16px",
    padding: "48px 40px",
    width: "360px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  titleArea: {
    textAlign: "center",
    marginBottom: "8px",
  },
  logo: {
    width: "52px",
    height: "52px",
    borderRadius: "14px",
    objectFit: "contain",
    margin: "0 auto 12px",
    display: "block",
  },
  title: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "700",
    color: "#1a1a2e",
  },
  subtitle: {
    margin: "6px 0 0",
    fontSize: "13px",
    color: "#888",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#444",
    alignSelf: "flex-start",
  },
  select: {
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fafafa",
    cursor: "pointer",
  },
  input: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fafafa",
  },
  error: {
    margin: 0,
    fontSize: "13px",
    color: "#e53935",
    textAlign: "center",
  },
  button: {
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#10134F",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    marginTop: "4px",
  },
};

export default EstimateLogin;
